import React from "react";
import { getImageUrl } from "../../Utils/helpers";
import * as C from "./style";
import userImg from "../../Assets/Images/user-profile-img.webp";
import { MdVerified } from "react-icons/md";
import * as I from "../../Config/interface.config";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import { markMessagesAsRead, selectChannel } from "../../Redux/slices/messageSlice";
import { Capitalize } from "../../Utils";

interface Prop {
  channeluser: I.UserDetails;
  channel: I.Channel;
  userId: string;
  onSelectUser?: () => void;
  showCreatorPresence?: boolean;
  isCreatorOnline?: boolean;
}

export const getMessageSenderId = (message?: I.Message) => {
  if (!message) return "";
  if (typeof message.from === "string") return message.from;
  return message.from?._id || message.from?.id || "";
};

export const getLatestMessage = (messages?: I.Message[]) => {
  if (!messages || messages.length === 0) return undefined;
  return [...messages].sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  )[0];
};

export const getChannelLatestTime = (channel: I.Channel) => {
  const latestMessage = getLatestMessage(channel.messages);
  return new Date(
    latestMessage?.createdAt || channel.lastMessageOn || channel.updatedAt || 0
  ).getTime();
};

export const getChannelRequiresReply = (channel: I.Channel, userId: string) => {
  const latestMessage = getLatestMessage(channel.messages);
  const latestSenderId = getMessageSenderId(latestMessage);
  return Boolean(latestSenderId && userId && latestSenderId !== userId);
};

const ChatUser: React.FC<Prop> = ({
  channeluser,
  channel,
  userId,
  onSelectUser,
  showCreatorPresence = false,
  isCreatorOnline = false,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { activeChat } = useSelector((state: RootState) => state.message);

  const userToDisplay = channeluser;
  const requiresReply = getChannelRequiresReply(channel, userId);
  const unreadCount = typeof channel.unreadCount === "number" ? channel.unreadCount : 0;
  const shouldShowBadge = requiresReply || unreadCount > 0;

  const handleClick = () => {
    dispatch(selectChannel({ channelSlug: channel?.channelSlug }));
    dispatch(markMessagesAsRead({ channelSlug: channel?.channelSlug }));
    if (onSelectUser) onSelectUser();
  };

  return (
    <C.ChatUser
      className={`${activeChat === channel?.channelSlug ? "bg-[#8B8B8B]" : ""}`}
      onClick={handleClick}
      tabIndex={0}
      aria-selected={activeChat === channel?.channelSlug}
      role="button"
    >
      <C.ImageWrapper>
        <C.UserImage
          alt="user"
          src={getImageUrl(userToDisplay?.image?.fileURL ?? "") || userImg}
          onError={(event) => {
            if (event.currentTarget.src !== userImg) {
              event.currentTarget.src = userImg;
            }
          }}
        />
        {showCreatorPresence && isCreatorOnline && (
          <C.OnlineDot aria-label="Creator is online" title="Online" />
        )}
        {shouldShowBadge && (
          <C.UnreadBadge title={requiresReply ? "Awaiting your reply" : "Unread messages"}>
            {unreadCount > 0 ? (unreadCount > 9 ? "9+" : unreadCount) : "1"}
          </C.UnreadBadge>
        )}
      </C.ImageWrapper>

      <C.UserDetail>
        <C.UserName>
          {Capitalize(userToDisplay?.fullname || "")}
          <span>
            {userToDisplay?.isVerified && <MdVerified title="Verified User" />}
          </span>
        </C.UserName>
        <C.ChatContent>
          {showCreatorPresence && isCreatorOnline && (
            <C.CreatorPresenceText $online>
              Online
            </C.CreatorPresenceText>
          )}
        </C.ChatContent>
      </C.UserDetail>
    </C.ChatUser>
  );
};

export default React.memo(ChatUser);
